import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Typography,
  Box,
  Dialog,
  Alert,
  AlertTitle,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useTranslation } from 'react-i18next';
import { EmailVerificationStateEnum } from '../../hooks/useEmailVerification';

interface EmailVerificationModalProps {
  readonly open: boolean;
  readonly handleClose: () => void;
  readonly sendVerificationEmail: () => Promise<void>;
  readonly state: EmailVerificationStateEnum;
}

export default function EmailVerificationModal({
  open,
  handleClose,
  sendVerificationEmail,
  state,
}: EmailVerificationModalProps) {
  const { t } = useTranslation();
  const defaultTimeOut = 30;
  const [timeRemaining, setTimeRemaining] = useState(defaultTimeOut);

  const resendVerificationEmail = useCallback(async () => {
    await sendVerificationEmail();
  }, [sendVerificationEmail]);

  useEffect(() => {
    if (open && state === EmailVerificationStateEnum.EMAIL_SENT) {
      const timer = setInterval(() => {
        setTimeRemaining((prevTime) => prevTime - 1);
      }, 1000);

      return () => {
        clearInterval(timer);
      };
    } else {
      setTimeRemaining(defaultTimeOut);
      return undefined;
    }
  }, [open, state]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='email-verification-modal-title'
      aria-describedby='email-verification-modal-description'
      maxWidth='xs'
      sx={{ zIndex: 1302 }}
    >
      <Box
        sx={{
          paddingX: 3,
          paddingY: 2.5,
          textAlign: 'center',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Box
          sx={{
            marginY: 1,
          }}
        >
          <MailOutlineIcon fontSize='large' />
          <Typography id='email-verification-modal-title' variant='h3'>
            {t('verifyEmail.checkYourEmail')}
          </Typography>
        </Box>
        <Typography id='email-verification-modal-description' variant='body1'>
          {t('verifyEmail.instructionsSentToEmail')}
        </Typography>
        <Box
          sx={{
            marginY: 2,
          }}
        >
          <Typography variant='body1'>
            {t('verifyEmail.didntReceiveMessage')}
          </Typography>
          {state === EmailVerificationStateEnum.ERROR && (
            <Alert
              severity='error'
              variant='outlined'
              sx={{ textAlign: 'left', marginY: 2 }}
            >
              <AlertTitle>
                {t('verifyEmail.errorSendingEmail.title')}
              </AlertTitle>
              <Typography variant='body1'>
                {t('verifyEmail.errorSendingEmail.description')}
              </Typography>
            </Alert>
          )}
          <Button
            disabled={
              state !== EmailVerificationStateEnum.ERROR && timeRemaining > 0
            }
            variant='text'
            color='primary'
            sx={{ mt: 1 }}
            onClick={resendVerificationEmail}
            aria-live='polite'
            loadingPosition='end'
            loading={state === EmailVerificationStateEnum.SENDING_EMAIL}
          >
            {state === EmailVerificationStateEnum.EMAIL_SENT &&
            timeRemaining > 0
              ? `${t('verifyEmail.orderNewConfirmationEmail')} (${timeRemaining})`
              : t('verifyEmail.orderNewConfirmationEmail')}
          </Button>
        </Box>
        <Button variant='contained' sx={{ mt: 3 }} onClick={handleClose}>
          {t('verifyEmail.close')}
        </Button>
      </Box>
    </Dialog>
  );
}
